import React, { useEffect, useState } from "react";
import { api, V1 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CreditCard, Apple, Smartphone, CheckCircle2, XCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function StatusBadge({ ok, label }) {
  return (
    <span
      data-testid={ok ? "integration-status-configured" : "integration-status-not-configured"}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function CopyField({ value, testId }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 font-mono">
      <span className="truncate">{value}</span>
      <button
        type="button"
        data-testid={testId}
        onClick={() => { navigator.clipboard.writeText(value); toast.success("Kopyalandı"); }}
        className="ml-auto shrink-0 text-slate-400 hover:text-slate-700"
      >
        <Copy className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export default function AdminIntegrations() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState("");

  const [iyzico, setIyzico] = useState({ api_key: "", secret_key: "", mode: "live" });
  const [apple, setApple] = useState({ shared_secret: "", bundle_id: "", apple_id: "" });
  const [google, setGoogle] = useState({ service_account_json: "", package_name: "", pubsub_audience: "", pubsub_email: "" });

  const load = async () => {
    const { data } = await api.get(`${V1}/admin/integration-settings`);
    setSettings(data);
    setApple((s) => ({ ...s, bundle_id: data.apple_iap.bundle_id || "", apple_id: data.apple_iap.apple_id || "" }));
    setGoogle((s) => ({ ...s, package_name: data.google_iap.package_name || "", pubsub_audience: data.google_iap.pubsub_audience || "", pubsub_email: data.google_iap.pubsub_email || "" }));
  };

  useEffect(() => { load(); }, []);

  const save = async (provider, body, setter) => {
    setBusy(provider);
    try {
      await api.put(`${V1}/admin/integration-settings/${provider}`, body);
      toast.success("Ayarlar kaydedildi");
      setter((s) => ({ ...s, api_key: "", secret_key: "", shared_secret: "", service_account_json: "" }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBusy("");
    }
  };

  if (!settings) return <div className="p-6 text-sm text-slate-500" data-testid="integrations-loading">Yükleniyor...</div>;

  return (
    <div className="space-y-8" data-testid="admin-integrations-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Entegrasyon Ayarları</h1>
        <p className="text-sm text-slate-500 mt-1">Canlı ödeme ve mobil mağaza anahtarlarını buradan ekleyin — kod değişikliği ya da yeniden başlatma gerekmez.</p>
      </div>

      {/* İYZİCO */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="integration-card-iyzico">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-indigo-600" />
            <h2 className="font-semibold text-slate-900">İyzico (Web Ödemeleri)</h2>
          </div>
          <StatusBadge ok={settings.iyzico.configured} label={settings.iyzico.configured ? `Canlı (${settings.iyzico.mode})` : "Test Modu"} />
        </div>
        <p className="text-xs text-slate-500">Anahtarlar iyzico Merchant Panel &rarr; Ayarlar &rarr; API Anahtarları&apos;ndan alınır. Kaydettiğinizde web ödemeleri otomatik canlıya geçer.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>API Key {settings.iyzico.api_key && <span className="text-slate-400 font-normal">(kayıtlı: {settings.iyzico.api_key})</span>}</Label>
            <Input data-testid="iyzico-api-key-input" type="password" value={iyzico.api_key} onChange={(e) => setIyzico({ ...iyzico, api_key: e.target.value })} placeholder="sandbox-xxxxx" />
          </div>
          <div>
            <Label>Secret Key {settings.iyzico.secret_key && <span className="text-slate-400 font-normal">(kayıtlı: {settings.iyzico.secret_key})</span>}</Label>
            <Input data-testid="iyzico-secret-key-input" type="password" value={iyzico.secret_key} onChange={(e) => setIyzico({ ...iyzico, secret_key: e.target.value })} placeholder="sandbox-xxxxx" />
          </div>
          <div>
            <Label>Mod</Label>
            <Select value={iyzico.mode} onValueChange={(v) => setIyzico({ ...iyzico, mode: v })}>
              <SelectTrigger data-testid="iyzico-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live (Canlı)</SelectItem>
                <SelectItem value="sandbox">Sandbox (Test)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button data-testid="iyzico-save-button" disabled={busy === "iyzico"} onClick={() => save("iyzico", iyzico, setIyzico)}>Kaydet</Button>
      </div>

      {/* APPLE */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="integration-card-apple">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Apple className="h-5 w-5 text-slate-800" />
            <h2 className="font-semibold text-slate-900">Apple In-App Purchase (iOS)</h2>
          </div>
          <StatusBadge ok={settings.apple_iap.webhook_configured} label={settings.apple_iap.webhook_configured ? "Yapılandırıldı" : "Bekliyor"} />
        </div>
        <p className="text-xs text-slate-500">App Store Connect &rarr; Uygulama &rarr; App Store Server Notifications V2 bölümüne bu URL&apos;yi ekleyin:</p>
        <CopyField testId="apple-webhook-url-copy" value={`${BACKEND_URL}/api/v1/payments/iap/webhooks/apple`} />
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label>Bundle ID</Label>
            <Input data-testid="apple-bundle-id-input" value={apple.bundle_id} onChange={(e) => setApple({ ...apple, bundle_id: e.target.value })} placeholder="com.sanostudent.mobile" />
          </div>
          <div>
            <Label>App Apple ID (opsiyonel)</Label>
            <Input data-testid="apple-apple-id-input" value={apple.apple_id} onChange={(e) => setApple({ ...apple, apple_id: e.target.value })} placeholder="1234567890" />
          </div>
          <div>
            <Label>Shared Secret {settings.apple_iap.shared_secret && <span className="text-slate-400 font-normal">(kayıtlı: {settings.apple_iap.shared_secret})</span>}</Label>
            <Input data-testid="apple-shared-secret-input" type="password" value={apple.shared_secret} onChange={(e) => setApple({ ...apple, shared_secret: e.target.value })} placeholder="App-Specific Shared Secret" />
          </div>
        </div>
        <Button data-testid="apple-save-button" disabled={busy === "apple"} onClick={() => save("apple", apple, setApple)}>Kaydet</Button>
      </div>

      {/* GOOGLE */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4" data-testid="integration-card-google">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-emerald-600" />
            <h2 className="font-semibold text-slate-900">Google Play Billing (Android)</h2>
          </div>
          <StatusBadge ok={settings.google_iap.webhook_configured} label={settings.google_iap.webhook_configured ? "Yapılandırıldı" : "Bekliyor"} />
        </div>
        <p className="text-xs text-slate-500">Play Console &rarr; Monetization setup &rarr; Real-time developer notifications içinde Pub/Sub konusu oluşturup push subscription&apos;ı bu URL&apos;ye yönlendirin:</p>
        <CopyField testId="google-webhook-url-copy" value={`${BACKEND_URL}/api/v1/payments/iap/webhooks/google`} />
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Package Name</Label>
            <Input data-testid="google-package-name-input" value={google.package_name} onChange={(e) => setGoogle({ ...google, package_name: e.target.value })} placeholder="com.sanostudent.mobile" />
          </div>
          <div>
            <Label>Pub/Sub Audience (Push subscription URL)</Label>
            <Input data-testid="google-pubsub-audience-input" value={google.pubsub_audience} onChange={(e) => setGoogle({ ...google, pubsub_audience: e.target.value })} placeholder={`${BACKEND_URL}/api/v1/payments/iap/webhooks/google`} />
          </div>
          <div className="sm:col-span-2">
            <Label>Pub/Sub Service Account Email</Label>
            <Input data-testid="google-pubsub-email-input" value={google.pubsub_email} onChange={(e) => setGoogle({ ...google, pubsub_email: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label>Service Account JSON {settings.google_iap.service_account_json && <span className="text-slate-400 font-normal">(kayıtlı: {settings.google_iap.service_account_json})</span>}</Label>
            <Textarea data-testid="google-service-account-json-input" rows={4} value={google.service_account_json} onChange={(e) => setGoogle({ ...google, service_account_json: e.target.value })} placeholder='{"type": "service_account", ...}' />
          </div>
        </div>
        <Button data-testid="google-save-button" disabled={busy === "google"} onClick={() => save("google", google, setGoogle)}>Kaydet</Button>
      </div>
    </div>
  );
}
